// Text of the privacy (/privacy) and contact (/contact) pages. Kept out of the
// i18n dictionary because it is long and web-only; the dictionary is shared
// verbatim with the mobile app.
//
// Who runs the site and how to reach them. Both pages, in both languages, read
// these two values. GDPR requires the controller's real identity here, so a
// brand name ("EventHunter Team") alone is not enough.
export const OPERATOR = {
  name: 'Márkus Martin',
  email: 'hello@eventhunter.app'
};

export const LAST_UPDATED = '2026-09-26';

export const UPDATED_LABEL = {
  hu: 'Utolsó frissítés:',
  en: 'Last updated:'
};

// Blocks render in order: { heading, paragraphs?, list? }. The tokens {name}
// and {email} are replaced with OPERATOR (the email becomes a mailto link),
// and {privacy} with a link to the privacy page.
const content = {
  hu: {
    privacy: {
      title: 'Adatvédelmi tájékoztató',
      intro:
        'Röviden: nincs regisztráció, nincs követő vagy reklám süti, és nem építünk rólad profilt. Az alábbiakban pontosan leírjuk, milyen adat keletkezik az oldal használatakor.',
      blocks: [
        {
          heading: 'Ki kezeli az adatokat?',
          paragraphs: [
            'Az EventHunter (eventhunter.app) üzemeltetője és az adatkezelő: {name}. Elérhetőség: {email}.'
          ]
        },
        {
          heading: 'Amit csak a böngésződ tárol',
          paragraphs: [
            'A kedvenceidet, a választott nyelvet és várost a böngésződ tárolja (localStorage), hogy legközelebb is meglegyenek. Ezeket mi nem mentjük el. A kedvencek aktuális adatait az oldal a szerverünktől kéri le, de a kérést nem tároljuk. Törölheted őket a böngésződben az oldal adatainak törlésével.'
          ]
        },
        {
          heading: 'Keresési statisztika',
          paragraphs: [
            'Amikor keresel vagy szűrsz, eltároljuk:'
          ],
          list: [
            'a választott szűrőket (város, időszak, kategória, műfaj, „csak ingyenes”),',
            'a keresőmezőbe írt szöveget,',
            'a találatok számát, a platformot (web) és az időpontot,',
            'a böngésződ típusát jelző szöveget (user agent).'
          ]
        },
        {
          paragraphs: [
            'Nem tárolunk mellé nevet, IP-címet vagy bármilyen azonosítót, így a keresések nem köthetők össze egymással és hozzád sem. A célunk, hogy lássuk, milyen programokat keresnek az emberek, és ez alapján bővítsük a kínálatot. Jogalap: jogos érdek (GDPR 6. cikk (1) f). Az adatok 90 nap után automatikusan törlődnek. Kérjük, ne írj személyes adatot a keresőmezőbe.'
          ]
        },
        {
          heading: 'Helyadat',
          paragraphs: [
            'Csak akkor, ha a „Legközelebbi hozzám” rendezést választod, és ezt a böngésződben engedélyezed. A koordinátáidat a távolságok kiszámításához elküldjük a szerverünknek, de nem tároljuk őket. Az engedélyt bármikor visszavonhatod a böngésződ beállításaiban.'
          ]
        },
        {
          heading: 'Látogatottsági statisztika',
          paragraphs: [
            'A látogatások számát a Cloudflare Web Analytics méri, amely nem használ sütit, és nem azonosítja a látogatókat.'
          ]
        },
        {
          heading: 'Szervernaplók',
          paragraphs: [
            'Mint minden weboldalnál, a tárhelyszolgáltatóink biztonsági és hibakeresési célból automatikusan naplózzák a technikai adatokat (IP-cím, időpont, lekért oldal), és ezeket rövid ideig megőrzik.'
          ]
        },
        {
          heading: 'Szolgáltatók, akiket igénybe veszünk',
          list: [
            'Cloudflare, Inc. – a weboldal kiszolgálása, DNS, látogatottsági statisztika',
            'Render Services, Inc. – a szerver futtatása (Frankfurt, EU)',
            'MongoDB, Inc. – adatbázis (MongoDB Atlas)'
          ]
        },
        {
          paragraphs: [
            'Ha adat az EU-n kívülre kerül, azt a szolgáltatók által biztosított garanciák (például az Európai Bizottság által elfogadott általános adatvédelmi kikötések) védik. Adatot nem adunk el, és reklámcélra nem adunk tovább.'
          ]
        },
        {
          heading: 'A programok adatai',
          paragraphs: [
            'A programok leírása és képei a helyszínek és szervezők nyilvános oldalairól származnak, a jogok az övéik. Ha jogtulajdonosként javítást vagy eltávolítást kérsz, írj nekünk: {email}.'
          ]
        },
        {
          heading: 'Jogaid',
          paragraphs: [
            'Kérhetsz tájékoztatást, helyesbítést, törlést vagy korlátozást, és tiltakozhatsz az adatkezelés ellen: {email}. Mivel a keresési statisztika nem tartalmaz azonosítót, azt általában nem tudjuk hozzád kötni. Panaszt a Nemzeti Adatvédelmi és Információszabadság Hatóságnál (NAIH, 1055 Budapest, Falk Miksa utca 9–11., naih.hu) tehetsz.'
          ]
        },
        {
          heading: 'Változások',
          paragraphs: [
            'Ha az adatkezelés változik, ezt az oldalt frissítjük; a fenti dátum mutatja az utolsó módosítást.'
          ]
        }
      ]
    },
    contact: {
      title: 'Kapcsolat',
      intro:
        'Kérdésed, ötleted van, hiányzik egy program, vagy hibás adatot találtál? Írj nekünk, szívesen olvassuk.',
      blocks: [
        {
          heading: 'E-mail',
          paragraphs: ['{email}']
        },
        {
          heading: 'Helyszíneknek és szervezőknek',
          paragraphs: [
            'Szeretnéd, hogy a programjaid megjelenjenek, javítanál egy adatot, vagy eltávolítanál valamit? Írj a fenti címre, és gyorsan intézzük.'
          ]
        },
        {
          heading: 'Impresszum',
          list: [
            'Üzemeltető: {name}',
            'E-mail: {email}',
            'Tárhelyszolgáltató: Cloudflare, Inc. (cloudflare.com) és Render Services, Inc. (render.com)'
          ]
        },
        {
          heading: 'Adatvédelem',
          paragraphs: ['Arról, hogy milyen adatot kezelünk, itt olvashatsz: {privacy}.']
        }
      ]
    }
  },

  en: {
    privacy: {
      title: 'Privacy policy',
      intro:
        "In short: there are no accounts, no tracking or advertising cookies, and we don't build a profile of you. Below is exactly what data is created when you use the site.",
      blocks: [
        {
          heading: 'Who is responsible',
          paragraphs: [
            'EventHunter (eventhunter.app) is run by, and the data controller is: {name}. Contact: {email}.'
          ]
        },
        {
          heading: 'What stays in your browser',
          paragraphs: [
            "Your favourites, language and chosen city are stored by your browser (localStorage) so they're there next time. We don't save them. To show up-to-date details of your favourites, the site requests them from our server, but that request isn't stored. You can delete them by clearing this site's data in your browser."
          ]
        },
        {
          heading: 'Search statistics',
          paragraphs: ['When you search or filter, we store:'],
          list: [
            'the filters you chose (city, dates, category, genre, "free only"),',
            'the text typed into the search box,',
            'the number of results, the platform (web) and the time,',
            "your browser's identification string (user agent)."
          ]
        },
        {
          paragraphs: [
            "We don't store a name, an IP address or any identifier with it, so searches can't be linked to each other or to you. We use this to see what people look for and to grow the catalogue accordingly. Legal basis: legitimate interest (GDPR Art. 6(1)(f)). The data is deleted automatically after 90 days. Please don't type personal information into the search box."
          ]
        },
        {
          heading: 'Location',
          paragraphs: [
            'Only if you choose "Nearest to me" and allow it in your browser. Your coordinates are sent to our server to calculate distances and are not stored. You can withdraw the permission at any time in your browser settings.'
          ]
        },
        {
          heading: 'Visitor statistics',
          paragraphs: [
            "Visits are counted with Cloudflare Web Analytics, which uses no cookies and doesn't identify visitors."
          ]
        },
        {
          heading: 'Server logs',
          paragraphs: [
            'As on any website, our hosting providers automatically log technical data (IP address, time, requested page) for security and troubleshooting, and keep it for a short time.'
          ]
        },
        {
          heading: 'Service providers we use',
          list: [
            'Cloudflare, Inc. - serving the website, DNS, visitor statistics',
            'Render Services, Inc. - running the server (Frankfurt, EU)',
            'MongoDB, Inc. - database (MongoDB Atlas)'
          ]
        },
        {
          paragraphs: [
            "Where data leaves the EU, it is protected by the safeguards these providers offer (such as the EU standard contractual clauses). We don't sell data or pass it on for advertising."
          ]
        },
        {
          heading: 'Event information',
          paragraphs: [
            "Event details and images come from the venues' and organisers' public websites, and the rights remain theirs. If you are a rights holder and want something corrected or removed, write to {email}."
          ]
        },
        {
          heading: 'Your rights',
          paragraphs: [
            "You can ask for access, correction, erasure or restriction, and object to processing: {email}. Because search statistics carry no identifier, we generally can't link them to you. You can lodge a complaint with the Hungarian data protection authority (NAIH, 1055 Budapest, Falk Miksa utca 9-11, naih.hu)."
          ]
        },
        {
          heading: 'Changes',
          paragraphs: [
            'If our data handling changes, we update this page; the date above shows the last change.'
          ]
        }
      ]
    },
    contact: {
      title: 'Contact',
      intro:
        "A question or an idea, an event we're missing, or wrong details somewhere? Write to us - we'd love to hear from you.",
      blocks: [
        {
          heading: 'Email',
          paragraphs: ['{email}']
        },
        {
          heading: 'For venues and organisers',
          paragraphs: [
            'Want your events listed, a detail corrected or something removed? Write to the address above and we will sort it out quickly.'
          ]
        },
        {
          heading: 'Legal notice',
          list: [
            'Operator: {name}',
            'Email: {email}',
            'Hosting: Cloudflare, Inc. (cloudflare.com) and Render Services, Inc. (render.com)'
          ]
        },
        {
          heading: 'Privacy',
          paragraphs: ['What data we handle is explained here: {privacy}.']
        }
      ]
    }
  }
};

export default content;
